import { createContext, useContext, useEffect, useState } from 'react';

interface DarkModeContextType {
  isDarkMode: boolean;
  setIsDarkMode: (isDark: boolean) => void;
}

const DarkModeContext = createContext<DarkModeContextType | undefined>(undefined);

export const DarkModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) {
        return JSON.parse(saved);
      }
      // Fallback to system preference when no saved preference exists
      if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
      }
      return false;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    // Toggle utility classes for Tailwind
    root.classList.toggle('dark', isDarkMode);
    body.classList.toggle('dark', isDarkMode);

    // Inform the UA for form controls and scrollbars
    root.style.colorScheme = isDarkMode ? 'dark' : 'light';

    // Update theme-color for mobile status bar (Android + some mobile browsers)
    const themeMeta = document.querySelector(
      'meta[name="theme-color"]'
    ) as HTMLMetaElement | null;
    if (themeMeta) {
      themeMeta.content = isDarkMode ? '#111827' : '#ffffff';
    }

    // Update iOS status bar style dynamically
    const appleStatusMeta = document.querySelector(
      'meta[name="apple-mobile-web-app-status-bar-style"]'
    ) as HTMLMetaElement | null;
    if (appleStatusMeta) {
      appleStatusMeta.content = isDarkMode ? 'black' : 'default';
    }

    try {
      localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
    } catch {}
  }, [isDarkMode]);

  // Optional: react to system theme changes live if user hasn't explicitly chosen
  useEffect(() => {
    if (!window.matchMedia) return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      const saved = localStorage.getItem('darkMode');
      // Only auto-switch if the user hasn't explicitly set a preference
      if (saved === null) {
        setIsDarkMode(e.matches);
      }
    };
    media.addEventListener?.('change', handler);
    return () => media.removeEventListener?.('change', handler);
  }, []);

  return (
    <DarkModeContext.Provider value={{ isDarkMode, setIsDarkMode }}>
      {children}
    </DarkModeContext.Provider>
  );
};

export const useDarkMode = () => {
  const context = useContext(DarkModeContext);
  if (context === undefined) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
};